import React from 'react';
import type { BoardFloorPlanData, FloorPlanItem, FloorPlanOpening, FloorPlanWall } from '../../../types';
import { wallEndPoint } from './floorPlanConstraints';
import FloorPlanGlyph from './FloorPlanGlyphs';
import {
  GRID_MAJOR_IN,
  GRID_MINOR_IN,
  formatInchesFtIn,
  garageDoorLayout,
  itemAabb,
  pointAlongWall,
  wallPerp,
  underlayCropHandleRects,
  underlayIsHidden,
  underlayIsLocked,
  underlayRotateHandle,
  underlayVisibleAabb,
  dimensionLabelBox,
  wallHandleSize,
  wallLength,
  wallSegments,
  wallUnit,
  type FloorPlanViewBox,
} from './floorPlanMath';
import { measureLengthIn } from './floorPlanDimensions';
import type { FloorPlanScaleRef, FloorPlanSelection } from './floorPlanTypes';
import './FloorPlanDrawing.css';

export interface FloorPlanDimMark {
  key: string;
  x: number;
  y: number;
  active?: boolean;
  hover?: boolean;
}

interface FloorPlanDrawingProps {
  data: BoardFloorPlanData;
  selected?: FloorPlanSelection | null;
  previewWalls?: Array<Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2' | 'thicknessIn'>>;
  ghostItem?: Pick<FloorPlanItem, 'catalogId' | 'x' | 'y' | 'widthIn' | 'depthIn' | 'rotation' | 'stair'> | null;
  showGrid?: boolean;
  showDimensions?: boolean;
  showHandles?: boolean;
  hideDimensionWallId?: string;
  snapHint?: { x: number; y: number } | null;
  scaleRef?: FloorPlanScaleRef | null;
  dimMarks?: FloorPlanDimMark[];
  measureLine?: { a: { x: number; y: number }; b: { x: number; y: number }; label?: string } | null;
  viewBox?: FloorPlanViewBox;
  pxPerIn?: number;
  /** Inline ink so a print/PDF rasterizer does not need stylesheets. */
  print?: boolean;
}

function doorLeaf(
  wall: FloorPlanWall,
  opening: FloorPlanOpening,
): { hinge: { x: number; y: number }; free: { x: number; y: number }; leaf: { x: number; y: number } } {
  const perp = wallPerp(wall);
  const inward = opening.flip
    ? { x: -perp.x, y: -perp.y }
    : perp;
  const hingeAtStart = opening.swing !== 'right';
  const hinge = pointAlongWall(wall, hingeAtStart ? opening.offsetIn : opening.offsetIn + opening.widthIn);
  const free = pointAlongWall(wall, hingeAtStart ? opening.offsetIn + opening.widthIn : opening.offsetIn);
  const leaf = {
    x: hinge.x + inward.x * opening.widthIn,
    y: hinge.y + inward.y * opening.widthIn,
  };
  return { hinge, free, leaf };
}

function gridValues(min: number, max: number, step: number): number[] {
  const start = Math.floor(min / step) * step;
  const values: number[] = [];
  for (let value = start; value <= max + 0.01; value += step) values.push(value);
  return values;
}

function WallLengthLabel({
  wall,
  className = '',
  viewBox,
  pxPerIn,
  print = false,
}: {
  wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2' | 'thicknessIn'>;
  className?: string;
  viewBox?: FloorPlanViewBox;
  pxPerIn?: number;
  print?: boolean;
}) {
  const length = wallLength(wall);
  if (length < 8) return null;
  const box = dimensionLabelBox(wall, viewBox, pxPerIn);
  return (
    <g className={className ? `fp-dim-label ${className}` : 'fp-dim-label'}>
      {!print && <rect className="fp-dim-hit" x={box.x} y={box.y} width={box.w} height={box.h} />}
      <text
        x={box.cx}
        y={box.cy}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={box.font}
        fill={print ? '#334155' : undefined}
        stroke="none"
      >
        {formatInchesFtIn(length)}
      </text>
    </g>
  );
}

const FloorPlanDrawing: React.FC<FloorPlanDrawingProps> = ({
  data,
  selected,
  previewWalls,
  ghostItem,
  showGrid = true,
  showDimensions = true,
  showHandles = false,
  hideDimensionWallId,
  snapHint,
  scaleRef,
  dimMarks,
  measureLine,
  viewBox,
  pxPerIn,
  print = false,
}) => {
  const { widthIn, depthIn } = data.footprint;
  const selectedId = selected?.id;
  const gridMinX = viewBox ? viewBox.x : 0;
  const gridMinY = viewBox ? viewBox.y : 0;
  const gridMaxX = viewBox ? viewBox.x + viewBox.w : widthIn;
  const gridMaxY = viewBox ? viewBox.y + viewBox.h : depthIn;
  const previews = (previewWalls || []).filter((wall) => wallLength(wall) >= 4);

  return (
    <g className="fp-drawing" color={print ? '#1e293b' : undefined} style={print ? { color: '#1e293b' } : undefined}>
      {showGrid && (
        <g className="fp-grid">
          {gridValues(gridMinX, gridMaxX, GRID_MINOR_IN).map((x) => (
            <line
              key={`vx-${x}`}
              className={x % GRID_MAJOR_IN === 0 ? 'fp-grid-major' : 'fp-grid-minor'}
              x1={x}
              y1={gridMinY}
              x2={x}
              y2={gridMaxY}
              stroke={print ? (x % GRID_MAJOR_IN === 0 ? '#94a3b8' : '#cbd5e1') : undefined}
              strokeWidth={print ? (x % GRID_MAJOR_IN === 0 ? 0.55 : 0.35) : undefined}
            />
          ))}
          {gridValues(gridMinY, gridMaxY, GRID_MINOR_IN).map((y) => (
            <line
              key={`hz-${y}`}
              className={y % GRID_MAJOR_IN === 0 ? 'fp-grid-major' : 'fp-grid-minor'}
              x1={gridMinX}
              y1={y}
              x2={gridMaxX}
              y2={y}
              stroke={print ? (y % GRID_MAJOR_IN === 0 ? '#94a3b8' : '#cbd5e1') : undefined}
              strokeWidth={print ? (y % GRID_MAJOR_IN === 0 ? 0.55 : 0.35) : undefined}
            />
          ))}
        </g>
      )}

      {data.walls.map((wall) => {
        const segs = wallSegments(wall, data.openings);
        const active = selected?.kind === 'wall' && selected.id === wall.id;
        return (
          <g key={wall.id} className={active ? 'fp-wall is-selected' : 'fp-wall'}>
            {segs.map((seg, i) => (
              <line
                key={`${wall.id}-${i}`}
                x1={seg.x1}
                y1={seg.y1}
                x2={seg.x2}
                y2={seg.y2}
                strokeWidth={wall.thicknessIn}
                stroke={print ? '#1e293b' : undefined}
                strokeLinecap="square"
                fill="none"
              />
            ))}
          </g>
        );
      })}

      {data.openings.map((opening) => {
        const wall = data.walls.find((w) => w.id === opening.wallId);
        if (!wall) return null;
        const active = selected?.kind === 'opening' && selected.id === opening.id;
        const start = pointAlongWall(wall, opening.offsetIn);
        const end = pointAlongWall(wall, opening.offsetIn + opening.widthIn);
        if (opening.kind === 'door') {
          const { hinge, free, leaf } = doorLeaf(wall, opening);
          return (
            <g
              key={opening.id}
              className={active ? 'fp-opening fp-door is-selected' : 'fp-opening fp-door'}
              fill="none"
              stroke={print ? '#0f172a' : undefined}
              strokeWidth={print ? 1.1 : undefined}
            >
              <path
                d={`M ${free.x} ${free.y} A ${opening.widthIn} ${opening.widthIn} 0 0 1 ${leaf.x} ${leaf.y}`}
                fill="none"
              />
              <line x1={hinge.x} y1={hinge.y} x2={leaf.x} y2={leaf.y} />
            </g>
          );
        }
        if (opening.kind === 'window') {
          const { ux, uy } = wallUnit(wall);
          const n = { x: -uy * 2.2, y: ux * 2.2 };
          return (
            <g
              key={opening.id}
              className={active ? 'fp-opening fp-window is-selected' : 'fp-opening fp-window'}
              fill="none"
              stroke={print ? '#0f172a' : undefined}
              strokeWidth={print ? 1.2 : undefined}
            >
              <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} />
              <line
                x1={start.x + n.x}
                y1={start.y + n.y}
                x2={end.x + n.x}
                y2={end.y + n.y}
              />
              <line
                x1={start.x - n.x}
                y1={start.y - n.y}
                x2={end.x - n.x}
                y2={end.y - n.y}
              />
            </g>
          );
        }
        const { half, face, jambInset, panels, inward, out } = garageDoorLayout(wall, opening);
        const along = (offset: number) => pointAlongWall(wall, offset);
        const shift = (p: { x: number; y: number }, nx: number, ny: number) => ({ x: p.x + nx, y: p.y + ny });
        const jamb = (p: { x: number; y: number }) => {
          const a = shift(p, inward.x * half, inward.y * half);
          const b = shift(p, out.x * half, out.y * half);
          return { a, b };
        };
        const startJamb = jamb(start);
        const endJamb = jamb(end);
        const innerStart = opening.offsetIn + jambInset;
        const innerEnd = opening.offsetIn + opening.widthIn - jambInset;
        const faceStart = shift(along(innerStart), out.x * (half + face), out.y * (half + face));
        const faceEnd = shift(along(innerEnd), out.x * (half + face), out.y * (half + face));
        return (
          <g
            key={opening.id}
            className={active ? 'fp-opening fp-garage is-selected' : 'fp-opening fp-garage'}
            fill="none"
            stroke={print ? '#0f172a' : undefined}
            strokeWidth={print ? 1.2 : undefined}
            strokeLinecap="butt"
          >
            <line x1={startJamb.a.x} y1={startJamb.a.y} x2={startJamb.b.x} y2={startJamb.b.y} />
            <line x1={endJamb.a.x} y1={endJamb.a.y} x2={endJamb.b.x} y2={endJamb.b.y} />
            <line x1={faceStart.x} y1={faceStart.y} x2={faceEnd.x} y2={faceEnd.y} />
            {Array.from({ length: panels + 1 }, (_, i) => {
              const p = along(innerStart + ((innerEnd - innerStart) * i) / panels);
              const a = shift(p, out.x * half, out.y * half);
              const b = shift(p, out.x * (half + face), out.y * (half + face));
              return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
            })}
          </g>
        );
      })}

      {data.items.map((item) => {
        const box = itemAabb(item);
        const active = selectedId === item.id && selected?.kind === 'item';
        return (
          <g
            key={item.id}
            className={active ? 'fp-item is-selected' : 'fp-item'}
            color={print ? '#1e293b' : undefined}
            transform={`translate(${box.x + box.w / 2} ${box.y + box.h / 2}) rotate(${item.rotation}) translate(${-item.widthIn / 2} ${-item.depthIn / 2})`}
          >
            <FloorPlanGlyph catalogId={item.catalogId} widthIn={item.widthIn} depthIn={item.depthIn} stair={item.stair} />
          </g>
        );
      })}

      {previews.map((wall, i) => (
        <line
          key={`preview-${i}`}
          className="fp-wall-preview"
          x1={wall.x1}
          y1={wall.y1}
          x2={wall.x2}
          y2={wall.y2}
          strokeWidth={wall.thicknessIn}
        />
      ))}

      {ghostItem && (
        <g
          className="fp-item is-ghost"
          transform={`translate(${ghostItem.x + ghostItem.widthIn / 2} ${ghostItem.y + ghostItem.depthIn / 2}) rotate(${ghostItem.rotation}) translate(${-ghostItem.widthIn / 2} ${-ghostItem.depthIn / 2})`}
        >
          <FloorPlanGlyph
            catalogId={ghostItem.catalogId}
            widthIn={ghostItem.widthIn}
            depthIn={ghostItem.depthIn}
            stair={ghostItem.stair}
          />
        </g>
      )}

      {selected?.kind === 'underlay' &&
        data.underlays?.map((underlay) => {
          if (underlay.id !== selected.id || underlayIsHidden(underlay)) return null;
          const box = underlayVisibleAabb(underlay);
          const handle = viewBox ? Math.max(6, viewBox.w * 0.012) : 10;
          const locked = underlayIsLocked(underlay);
          const lock = Math.max(10, handle * 1.4);
          return (
            <g key={`sel-${underlay.id}`} className={`fp-underlay-sel${locked ? ' is-locked' : ''}`}>
              <rect className="fp-underlay-frame" x={box.x} y={box.y} width={box.w} height={box.h} />
              {!locked &&
                underlayCropHandleRects(underlay, viewBox).map((crop) => (
                  <rect
                    key={crop.edge}
                    className={`fp-underlay-crop fp-underlay-crop--${crop.edge === 'left' || crop.edge === 'right' ? 'h' : 'v'}`}
                    x={crop.x}
                    y={crop.y}
                    width={crop.w}
                    height={crop.h}
                  />
                ))}
              {!locked && (
                <rect
                  className="fp-underlay-handle"
                  x={box.x + box.w - handle / 2}
                  y={box.y + box.h - handle / 2}
                  width={handle}
                  height={handle}
                />
              )}
              {!locked && (() => {
                const rotate = underlayRotateHandle(underlay, viewBox);
                return (
                  <g className="fp-underlay-rotate">
                    <line x1={box.x + box.w / 2} y1={box.y} x2={rotate.x} y2={rotate.y} />
                    <circle cx={rotate.x} cy={rotate.y} r={rotate.r} />
                  </g>
                );
              })()}
              {locked && (
                <g className="fp-underlay-lock" transform={`translate(${box.x + lock * 0.35} ${box.y + lock * 0.35})`}>
                  <rect x={0} y={lock * 0.38} width={lock} height={lock * 0.62} rx={lock * 0.12} />
                  <path
                    d={`M ${lock * 0.22} ${lock * 0.38} V ${lock * 0.28} A ${lock * 0.28} ${lock * 0.28} 0 0 1 ${lock * 0.78} ${lock * 0.28} V ${lock * 0.38}`}
                  />
                </g>
              )}
            </g>
          );
        })}

      {showHandles &&
        selected?.kind === 'wall' &&
        data.walls
          .filter((wall) => wall.id === selected.id)
          .map((wall) => {
            const handle = wallHandleSize(viewBox, true);
            return (
              <g key={`ends-${wall.id}`} className="fp-wall-ends is-selected">
                {(['start', 'end'] as const).map((end) => {
                  const point = wallEndPoint(wall, end);
                  return (
                    <rect
                      key={end}
                      className="fp-wall-handle"
                      x={point.x - handle / 2}
                      y={point.y - handle / 2}
                      width={handle}
                      height={handle}
                    />
                  );
                })}
              </g>
            );
          })}

      {snapHint && (
        <circle className="fp-snap-hint" cx={snapHint.x} cy={snapHint.y} r={viewBox ? Math.max(8, viewBox.w * 0.012) : 10} />
      )}

      {scaleRef?.a && (
        <g className="fp-scale-ref">
          {scaleRef.b && (
            <line x1={scaleRef.a.x} y1={scaleRef.a.y} x2={scaleRef.b.x} y2={scaleRef.b.y} />
          )}
          <circle cx={scaleRef.a.x} cy={scaleRef.a.y} r={viewBox ? Math.max(4, viewBox.w * 0.008) : 5} />
          {scaleRef.b && (
            <circle cx={scaleRef.b.x} cy={scaleRef.b.y} r={viewBox ? Math.max(4, viewBox.w * 0.008) : 5} />
          )}
        </g>
      )}

      {(dimMarks?.length || measureLine) && (
        <g className="fp-dim-marks">
          {measureLine && (
            <>
              <line
                className="fp-measure-line"
                x1={measureLine.a.x}
                y1={measureLine.a.y}
                x2={measureLine.b.x}
                y2={measureLine.b.y}
                fill="none"
                stroke={print ? '#4f46e5' : undefined}
              />
              {measureLine.label && (
                <text
                  className="fp-measure-label"
                  x={(measureLine.a.x + measureLine.b.x) / 2}
                  y={(measureLine.a.y + measureLine.b.y) / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={viewBox ? Math.max(8, viewBox.w * 0.018) : 10}
                  fill={print ? '#334155' : undefined}
                  stroke="none"
                >
                  {measureLine.label}
                </text>
              )}
            </>
          )}
          {(dimMarks || []).map((mark) => {
            const size = viewBox ? Math.max(2.6, viewBox.w * 0.006) : 3.2;
            return (
              <rect
                key={mark.key}
                className={`fp-dim-point${mark.active ? ' is-active' : ''}${mark.hover ? ' is-hover' : ''}`}
                x={mark.x - size / 2}
                y={mark.y - size / 2}
                width={size}
                height={size}
                transform={`rotate(45 ${mark.x} ${mark.y})`}
              />
            );
          })}
        </g>
      )}

      {(data.measures || []).map((measure) => {
        const len = measureLengthIn(measure);
        if (len < 0.5) return null;
        const active = selected?.kind === 'measure' && selected.id === measure.id;
        const mx = (measure.x1 + measure.x2) / 2;
        const my = (measure.y1 + measure.y2) / 2;
        const font = viewBox ? Math.max(8, viewBox.w * 0.016) : 10;
        const tick = viewBox ? Math.max(2.4, viewBox.w * 0.005) : 3;
        return (
          <g
            key={measure.id}
            className={active ? 'fp-tape is-selected' : 'fp-tape'}
            fill="none"
            stroke={print ? '#1e293b' : undefined}
            strokeWidth={print ? 1.1 : undefined}
          >
            <line x1={measure.x1} y1={measure.y1} x2={measure.x2} y2={measure.y2} />
            <circle cx={measure.x1} cy={measure.y1} r={tick} fill={print ? '#1e293b' : undefined} />
            <circle cx={measure.x2} cy={measure.y2} r={tick} fill={print ? '#1e293b' : undefined} />
            <text
              x={mx}
              y={my}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={font}
              fill={print ? '#334155' : undefined}
              stroke="none"
            >
              {formatInchesFtIn(len)}
            </text>
          </g>
        );
      })}

      {showDimensions && (
        <g className="fp-dims">
          {data.walls.filter((wall) => wall.id !== hideDimensionWallId).map((wall) => (
            <WallLengthLabel key={`dim-${wall.id}`} wall={wall} viewBox={viewBox} pxPerIn={pxPerIn} print={print} />
          ))}
          {previews.map((wall, i) => (
            <WallLengthLabel
              key={`pdim-${i}`}
              wall={wall}
              className="is-preview"
              viewBox={viewBox}
              pxPerIn={pxPerIn}
            />
          ))}
        </g>
      )}
    </g>
  );
};

export default FloorPlanDrawing;
